import { BaseCommand } from '../ICommand';
import { getNodeMutationManager } from '../mutations/NodeMutationManager';
import type { Edge } from '../../models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';
import { listDomainProfiles } from '../../data-profiles/profileRegistry';
import {
  resolveEdgeBinding,
  applyEntityFieldEdgeChange,
  removeBindingFromEdge,
} from '../../../domains/data-binding/utils/edgeBinding';
import { getDataBinding } from '../../../domains/data-binding/services/BoundShapeEditInterceptor';
import { getGravityUserId } from '../../../hooks/useAuth';
import {
  createEdgeId,
  createNodeId,
  type EdgeAddition,
} from '../../data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../data-profiles/scenarios/ScenarioRecordable';

export class DeleteEdgeCommand extends BaseCommand implements IScenarioRecordable {
  private edgeId: string;
  private prevEdge: Edge | null = null;
  readonly isScenarioRecordable = true as const;

  constructor(edgeId: string) {
    super('Delete Edge', `Delete edge ${edgeId}`);
    this.edgeId = edgeId;
  }

  execute(): void {
    const mutationManager = getNodeMutationManager();
    const existing = mutationManager.getEdge(this.edgeId);
    if (!existing) return;
    this.prevEdge = { ...existing, attrs: { ...(existing.attrs || {}) } } as Edge;

    const doc = mutationManager.getDoc();
    if (doc) {
      const profile = listDomainProfiles().find((p) =>
        resolveEdgeBinding(existing.type as string, p) !== undefined
      );
      const bindingMapping = profile
        ? resolveEdgeBinding(existing.type as string, profile)
        : undefined;

      if (bindingMapping?.pattern === 'entity-field') {
        const ownerShapeId = bindingMapping.fieldOwner === 'from' ? existing.from : existing.to;
        const shapesMap = doc.getMap('shapes');
        const ownerShape = shapesMap.get(ownerShapeId) as Record<string, unknown> | undefined;
        const ownerBinding = ownerShape ? getDataBinding(ownerShape) : null;
        const sourceId = ownerBinding?.sourceId;
        if (sourceId) {
          const userId = getGravityUserId() ?? 'unknown';
          applyEntityFieldEdgeChange(doc, bindingMapping, existing.from, null, sourceId, userId);
        }
      } else if (bindingMapping?.pattern === 'edge-table') {
        removeBindingFromEdge(doc, this.edgeId);
      }
    }

    mutationManager.deleteEdge(this.edgeId);
  }

  undo(): void {
    if (!this.prevEdge) return;
    const mutationManager = getNodeMutationManager();
    mutationManager.addEdge(this.prevEdge);
  }

  private getEdgeSnapshot(): Edge | undefined {
    return useDiagramStore.getState().edges.find((edge) => edge.id === this.edgeId);
  }

  private toEdgeAddition(edge: Edge): EdgeAddition {
    return {
      id: createEdgeId(edge.id),
      type: (edge.type as string) ?? 'sequence_flow',
      from: createNodeId(edge.from),
      to: createNodeId(edge.to),
      props: {
        ...(edge.attrs ?? {}),
        fromAnchor: (edge as any).fromAnchor,
        toAnchor: (edge as any).toAnchor,
        fromOffset: (edge as any).fromOffset,
        toOffset: (edge as any).toOffset,
      },
    };
  }

  toScenarioDelta(): CommandDeltaContribution {
    return {
      edgeRemovals: [{ edgeId: createEdgeId(this.edgeId) }],
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    const edge = this.getEdgeSnapshot();
    return {
      edgeAdditions: edge ? [this.toEdgeAddition(edge)] : [],
    };
  }
}
